import type { LicenceModel } from '@kimuno/api-sdk/lib/types/responses/Billing';
import { Box, CircularProgress, Typography } from '@mui/material';
import config from 'config';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { palette, spaces } from '../../assets/styles/themes/tokens';
import type { StoreType } from '../../reducers';
import type { LicenceId } from '../../types/licences';
import type { ZuoraPaymentPageResponseHandler } from '../../types/ZuoraPayments';
import { notifyBugsnagHandledError } from '../../utils/bugsnagClient';

interface PaypalFormProps {
	licenceId: LicenceId;
	token?: string;
	onResponse: ZuoraPaymentPageResponseHandler;
}

type State = LicenceModel;

const checkoutPaymentTemplate = {
	displayName: 'meinUnterricht',
	flow: 'vault',
	intent: 'authorize',
	currency: 'EUR',
	locale: 'de_DE',
};

const PaypalForm: React.FC<PaypalFormProps & State> = (props) => {
	const { token, onResponse } = props;
	const { price, name } = props || { price: 0, name: '' };
	const [buttonIsLoaded, setButtonIsLoaded] = useState(false);
	const [hasRendered, setHasRendered] = useState(false);
	const { t } = useTranslation();

	const createPaypalButton = (token: string, productPrice: number, productName: string) => {
		const paypalRenderOptions = (checkout: any) => ({
			env: config.braintreeEnvironment,
			payment: () =>
				checkout.createPayment({
					...checkoutPaymentTemplate,
					amount: productPrice,
					billingAgreementDescription: productName,
				}),
			onAuthorize: (data: any) =>
				checkout.tokenizePayment(data).then((payload: any) => {
					onResponse('braintree', { auth: payload.nonce, success: true });
				}),
			onCancel: () => {},
			validate: () => {
				setButtonIsLoaded(true);
			},
			onError: () => {},
			style: {
				color: 'gold',
				size: 'medium',
				shape: 'rect',
				tagline: false,
			},
		});

		window.braintree.client
			.create({ authorization: token })
			.then((client: any) => window.braintree.paypalCheckout.create({ client }))
			.then((braintreeCheckout: any) => {
				return window.paypal.Button.render(
					paypalRenderOptions(braintreeCheckout),
					'#paypal-button-container'
				);
			})
			.catch((error: Error) => notifyBugsnagHandledError(error));
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (token && !hasRendered) {
			createPaypalButton(token, price, name);
			setHasRendered(true);
		}
	}, [token, hasRendered, price, name]);

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.md }}>
			<Typography variant="body2" color={palette.text.secondary}>
				{t('You allow meinUnterricht to take money from your paypal account.')}
			</Typography>
			<Box sx={{ display: 'flex', justifyContent: 'center' }}>
				<Box
					id="paypal-button-container"
					data-testid="paypal-button-container"
					sx={{
						display: !buttonIsLoaded ? 'none' : 'block',
					}}
				/>
				{!buttonIsLoaded && (
					<CircularProgress
						sx={{
							'& .MuiCircularProgress-svg': {
								height: 'auto',
								width: 'auto',
							},
						}}
					/>
				)}
			</Box>
		</Box>
	);
};

export default connect(
	(state: StoreType, props: PaypalFormProps): State => ({
		...(state.licenceModels.byId[props.licenceId] || {}),
	})
)(PaypalForm);
